import { useEffect, useState, type ComponentType } from 'react';
import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import {
  UserRound,
  Wallet,
  ReceiptText,
  Store,
  Building2,
  Coins,
  ClipboardList,
  CircleCheck,
  CreditCard,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { BrandMark } from '@/components/BrandMark';
import { SlimFooter } from '@/components/SlimFooter';
import { StepHeader } from '@/components/wizard/StepHeader';
import { SectionStepper, WizardMobileProgress } from '@/components/wizard/SectionStepper';
import { SummaryRail } from '@/components/wizard/SummaryRail';
import { NightAIDrawer } from '@/components/wizard/NightAIDrawer';
import { SubmittedScreen } from '@/components/wizard/SubmittedScreen';
import { onSectionRequest, type SectionId } from '@/components/wizard/section-bus';
import { PersonalSection } from '@/features/personal/PersonalSection';
import { IncomeSection } from '@/features/income/IncomeSection';
import { DeductionsSection } from '@/features/deductions/DeductionsSection';
import { SoleTraderSection } from '@/features/sole-trader';
import { RentalPropertySection } from '@/features/rental-property';
import { CryptoSection } from '@/features/crypto';
import { OtherSection } from '@/features/other/OtherSection';
import { ReviewSection } from '@/features/review/ReviewSection';
import { PaymentSection } from '@/features/payment/PaymentSection';
import { useTaxReturnStore } from '@/store/return-store';
import { useTaxEstimate } from '@/hooks/useTaxEstimate';
import { useSectionStatus } from '@/hooks/useSectionStatus';
import { flushPendingSaves } from '@/hooks/useAutoSave';
import { askNightAI, subscribeToAsk } from '@/lib/ai-bus';
import { cn, taxYearLabel } from '@/lib/utils';

export const Route = createFileRoute('/returns/$returnId')({
  component: ReturnPage,
});

interface StepDef {
  id: SectionId;
  /** Short label for the stepper. */
  label: string;
  icon: LucideIcon;
  /** Plain-language screen title + subtitle (design copy). */
  title: string;
  subtitle: string;
  /** Primary action label; null on the final step (Review lodges itself). */
  next: string | null;
  /** Optional "do this step with NightAI" affordance under the nav buttons —
   *  set on the high-drop-off steps. Clicking seeds the question via the
   *  ask-bus (which also opens the drawer) and the agent runs its scripted
   *  flow for that step. */
  aiAssist?: { label: string; question: string };
  Body: ComponentType<{ returnId: string }>;
}

/**
 * Whitelisted `?ask=` deep-link keys → seeded NightAI questions. Used by
 * re-engagement links (email nudges etc.): `/returns/42?ask=deductions#section-deductions`
 * lands on the step AND starts the guided conversation. A whitelist — never
 * raw text from the URL — so a crafted link can't put arbitrary words in the
 * user's mouth.
 */
const DEEP_LINK_ASKS: Record<string, string> = {
  income:
    'Help me get my income into the return. Pull my ATO prefill if you can; if there is nothing on file, walk me through entering it manually.',
  deductions:
    'Help me find my deductions. Ask me what applies to my occupation, one question at a time, and add each claim to my return as I answer.',
  finish: "What's left before I can lodge my return? Walk me through finishing it.",
};

/**
 * The guided return, one section per step. Each Body is an existing section
 * component; the wizard owns the plain-language header, stepper, summary rail,
 * and Back/Next. Add/refine sections here as we migrate them.
 */
const STEPS: StepDef[] = [
  {
    id: 'personal',
    label: 'Your details',
    icon: UserRound,
    title: "First, let's confirm who you are",
    subtitle: "We've matched these to your ATO record — just check they're right.",
    next: 'Save and continue',
    // No aiAssist here on purpose: personal details are prefilled from the ATO
    // record and mostly read-and-confirm — a chat handoff adds friction, not help.
    Body: PersonalSection,
  },
  {
    id: 'income',
    label: 'Income',
    icon: Wallet,
    title: "Let's check the money you earned",
    subtitle: "Your employer and bank already sent this to the ATO. Add anything they didn't have.",
    next: 'Looks right, continue',
    aiAssist: {
      label: 'Do this step with NightAI',
      question: DEEP_LINK_ASKS['income']!,
    },
    Body: IncomeSection,
  },
  {
    id: 'deductions',
    label: 'Deductions',
    icon: ReceiptText,
    title: "Now let's find money to claim back",
    subtitle: "Add what you spent for work — we'll estimate the claim for you.",
    next: 'Continue',
    aiAssist: {
      label: 'Find my deductions with NightAI',
      question: DEEP_LINK_ASKS['deductions']!,
    },
    Body: DeductionsSection,
  },
  {
    id: 'sole-trader',
    label: 'Sole trader',
    icon: Store,
    title: 'Do you have sole trader income?',
    subtitle: "Record your business income and expenses. Skip this step if it doesn't apply.",
    next: 'Continue',
    aiAssist: {
      label: 'Not sure? Ask NightAI',
      question:
        'Does the sole trader section apply to me? Ask me about any business or self-employed income I had this year, and help me record it if it applies.',
    },
    Body: SoleTraderSection,
  },
  {
    id: 'rental-property',
    label: 'Rental property',
    icon: Building2,
    title: 'Any rental property?',
    subtitle: 'Rent received and rental expenses for any investment properties.',
    next: 'Continue',
    aiAssist: {
      label: 'Do this step with NightAI',
      question:
        'Help me with the rental property section — ask me about any investment properties I earned rent from and record the income and expenses for each.',
    },
    Body: RentalPropertySection,
  },
  {
    id: 'crypto',
    label: 'Crypto',
    icon: Coins,
    title: 'Did you sell any crypto?',
    subtitle: 'Profit or loss and holding period for any crypto you sold this year.',
    next: 'Continue',
    aiAssist: {
      label: 'Not sure? Ask NightAI',
      question:
        'Help me with the crypto section — explain what counts as a taxable crypto event, ask me if any apply to me, and record the result.',
    },
    Body: CryptoSection,
  },
  {
    id: 'other',
    label: 'Other',
    icon: ClipboardList,
    title: 'Just a few last things',
    subtitle: 'Spouse, health cover, dependants — these help get your refund exactly right.',
    next: 'Continue',
    aiAssist: {
      label: 'Do this step with NightAI',
      question:
        'Walk me through the final details — spouse, Medicare and private health cover, dependants, income tests — and fill in what applies to me, one question at a time.',
    },
    Body: OtherSection,
  },
  {
    id: 'review',
    label: 'Review',
    icon: CircleCheck,
    title: "Here's your return, all done",
    subtitle: "Have a final look. When you're happy, continue to payment.",
    next: null,
    aiAssist: {
      label: "What's left? Ask NightAI",
      question: DEEP_LINK_ASKS['finish']!,
    },
    Body: ReviewSection,
  },
  {
    id: 'payment',
    label: 'Payment',
    icon: CreditCard,
    title: 'Last step — your fee',
    subtitle: 'Choose how to pay, then we lodge your return with the ATO.',
    next: null,
    Body: PaymentSection,
  },
];

const STEP_IDS = STEPS.map((s) => s.id);

function initialStep(): SectionId {
  if (typeof window === 'undefined') return 'personal';
  const hash = window.location.hash.replace(/^#section-/, '').replace(/^#/, '');
  return (STEP_IDS as string[]).includes(hash) ? (hash as SectionId) : 'personal';
}

function ReturnPage() {
  const { returnId } = useParams({ from: '/returns/$returnId' });
  const navigate = useNavigate();
  const setReturn = useTaxReturnStore((s) => s.setReturn);
  const { year, estimatedRefund } = useTaxReturnStore();
  const lodgedAt = useTaxReturnStore((s) => s.lodgedAt);
  const sectionErrors = useTaxReturnStore((s) => s.sections);
  const { status } = useSectionStatus(returnId);

  // Live refund/owing estimate — server calc, initial GET + SSE stream. Its
  // breakdown feeds the summary rail's "return so far" income/deductions.
  const estimate = useTaxEstimate(returnId);

  const [stepId, setStepId] = useState<SectionId>(() => initialStep());
  const [aiOpen, setAiOpen] = useState(false);
  // The floating NightAI launcher introduces itself: on first appearance it shows
  // the "Ask NightAI" label, then shrinks to the owl so it stops covering form
  // content. Once the user has met it (auto-shrink) or dismissed it with ×, it
  // stays owl-only — the × choice is remembered across visits.
  const [fabIntroduced, setFabIntroduced] = useState<boolean>(() => {
    try {
      return localStorage.getItem('nighttax-ai-fab-introduced') === '1';
    } catch {
      return false;
    }
  });
  const [fabLabelShown, setFabLabelShown] = useState(false);
  useEffect(() => {
    if (fabIntroduced) return;
    setFabLabelShown(true);
    // Auto-shrink is the ONLY dismissal now — the old × button next to the
    // label looked broken on mobile and dismissing a helper isn't a choice
    // users need to make. Persist "introduced" here so the label only ever
    // introduces itself once.
    const timer = setTimeout(() => {
      setFabLabelShown(false);
      setFabIntroduced(true);
      try {
        localStorage.setItem('nighttax-ai-fab-introduced', '1');
      } catch {
        /* ignore */
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [fabIntroduced]);

  useEffect(() => {
    setReturn(returnId, 2026);
  }, [returnId, setReturn]);

  // The agent (and any link) can request a section — flush pending saves, then
  // make it the active step (so the current section's edits aren't dropped).
  useEffect(
    () => onSectionRequest((id) => void flushPendingSaves().then(() => setStepId(id))),
    [],
  );

  // "Ask NightAI about this section" buttons open the drawer; CopilotRail
  // subscribes separately to append the message and run the turn.
  useEffect(() => subscribeToAsk(() => setAiOpen(true)), []);

  // Deep-link seeding: `?ask=<key>` starts a guided NightAI conversation on
  // arrival — used by re-engagement links so "finish your deductions" lands in
  // the interview, not on a blank form. The param is stripped immediately so a
  // refresh doesn't re-trigger; the bus's consume-once semantics cover the rest.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get('ask');
    if (!key) return;
    params.delete('ask');
    const qs = params.toString();
    history.replaceState(
      null,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`,
    );
    const question = DEEP_LINK_ASKS[key];
    if (question) askNightAI(question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funnel beacon: one structured event per step entry so drop-off is
  // measurable per step (queryable in Seq as `FunnelStep`). Fire-and-forget —
  // analytics must never affect the user. StrictMode double-fires this in dev;
  // dedupe at query time, not here.
  useEffect(() => {
    void fetch(`/api/v1/returns/${returnId}/funnel/step`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step: stepId }),
    }).catch(() => {});
  }, [returnId, stepId]);

  // Once lodged, the wizard gives way to the confirmation screen.
  if (lodgedAt) {
    return (
      <div className="flex min-h-screen flex-col bg-(--color-paper)">
        <AppHeader
          authed
          crumbs={[
            { label: 'My returns', to: '/returns' },
            { label: `Tax return ${taxYearLabel(year)}` },
          ]}
        />
        <main id="main" className="flex-1">
          <SubmittedScreen
            refund={estimatedRefund}
            lodgedAt={lodgedAt}
            returnId={returnId}
            onDone={() => navigate({ to: '/returns' })}
          />
        </main>
        <SlimFooter />
      </div>
    );
  }

  const goto = async (id: SectionId) => {
    // Flush the current section's pending autosave before it unmounts, so the
    // last few hundred ms of typing can't be dropped on the step change.
    await flushPendingSaves();
    setStepId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (typeof history !== 'undefined') history.replaceState(null, '', `#section-${id}`);
  };

  const idx = Math.max(0, STEPS.findIndex((s) => s.id === stepId));
  // idx is clamped to a valid index above, so STEPS[idx] is always defined.
  const step = STEPS[idx]!;
  const prev = idx > 0 ? STEPS[idx - 1] : null;
  const next = idx < STEPS.length - 1 ? STEPS[idx + 1] : null;
  const Body = step.Body;

  const errorCounts: Record<string, number> = {};
  const sectionComplete: Record<string, boolean> = {};
  for (const s of STEPS) {
    errorCounts[s.id] = sectionErrors[s.id]?.errorCount ?? 0;
    sectionComplete[s.id] = status[s.id];
  }

  return (
    <div className="flex min-h-screen flex-col bg-(--color-paper)">
      <AppHeader
        authed
        crumbs={[
          { label: 'My returns', to: '/returns' },
          { label: `Tax return ${taxYearLabel(year)}` },
        ]}
      />

      <WizardMobileProgress steps={STEPS} activeId={stepId} refund={estimatedRefund} />

      <main id="main" className="flex-1">
        <div className="mx-auto flex max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:gap-10 lg:py-10">
          {/* Left: vertical stepper */}
          <aside className="hidden w-[240px] shrink-0 lg:block">
            <div className="sticky top-8">
              <SectionStepper
                steps={STEPS}
                activeId={stepId}
                complete={sectionComplete}
                errorCounts={errorCounts}
                onSelect={(id) => goto(id as SectionId)}
              />
            </div>
          </aside>

          {/* Center: current step */}
          <div className="min-w-0 flex-1">
            <div className="mx-auto max-w-[680px]">
              <StepHeader title={step.title} subtitle={step.subtitle} />

              {/* Top placement on purpose: at the bottom (under the nav buttons)
                  users left the step without ever seeing it. A quiet chip, so it
                  coexists with the louder empty-state rescue cards inside
                  Income/Deductions. */}
              {step.aiAssist && (
                <button
                  type="button"
                  onClick={() => askNightAI(step.aiAssist!.question)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-(--color-navy-soft) bg-(--color-navy-soft)/40 px-3.5 py-1.5 text-[13px] font-semibold text-(--color-navy) transition-colors hover:bg-(--color-navy-soft)"
                >
                  <Sparkles className="size-3.5" strokeWidth={2} />
                  {step.aiAssist.label}
                </button>
              )}

              <div id={`section-${step.id}`} className="mt-6 scroll-mt-24">
                <Body returnId={returnId} />
              </div>

              <div className="mt-8 flex items-center gap-3">
                {prev ? (
                  <button
                    type="button"
                    onClick={() => goto(prev.id)}
                    className="inline-flex h-11 items-center gap-2 rounded-(--radius) border-[1.5px] border-(--color-border-strong) bg-(--color-card) px-5 text-[15px] font-semibold text-(--color-ink) hover:bg-(--color-paper-2)"
                  >
                    <ArrowLeft className="size-4" /> Back
                  </button>
                ) : (
                  <span />
                )}
                {step.next && next && (
                  <button
                    type="button"
                    onClick={() => goto(next.id)}
                    className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-(--radius) bg-(--color-primary) px-6 text-[16px] font-bold text-(--color-primary-foreground) shadow-[0_6px_16px_rgb(29_95_214_/_0.22)] hover:bg-(--color-navy-2)"
                  >
                    {step.next}
                    <ArrowRight className="size-[18px]" strokeWidth={2.4} />
                  </button>
                )}
              </div>

              {step.id !== 'payment' && (
                <p className="mt-3 text-center text-[12.5px] font-medium text-(--color-ink-muted)">
                  Your answers save automatically — you can leave and come back any time.
                </p>
              )}
            </div>
          </div>

          {/* Right: summary rail + NightAI entry (wide screens) */}
          <aside className="hidden w-[300px] shrink-0 xl:block">
            <div className="sticky top-8 flex flex-col gap-3">
              <SummaryRail estimatedRefund={estimatedRefund} breakdown={estimate.data} />
              <button
                type="button"
                onClick={() => setAiOpen(true)}
                className="flex items-center gap-3 rounded-(--radius-lg) border border-(--color-navy-soft) bg-(--color-card) p-3.5 text-left shadow-[0_1px_2px_rgb(20_35_61_/_0.05)] hover:bg-(--color-navy-soft)"
              >
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ background: 'linear-gradient(140deg,#1d5fd6,#2f74ea)' }}
                >
                  <BrandMark monochrome className="size-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-bold text-(--color-ink)">Ask NightAI</span>
                  <span className="block text-[12px] font-medium text-(--color-ink-muted)">
                    Stuck? Get a plain-English answer.
                  </span>
                </span>
              </button>
            </div>
          </aside>
        </div>
      </main>

      <SlimFooter />

      {/* Floating launcher — only when the summary rail (which carries its own
          "Ask NightAI" card) is hidden, i.e. below xl. On xl+ the rail card is
          the entry point, so the FAB would be redundant. Minimising collapses the
          pill to an owl-only button so it never permanently blocks the assistant. */}
      {!aiOpen && (
        <div className="fixed bottom-5 right-5 z-30 xl:hidden">
          <div className="relative">
            <button
              type="button"
              onClick={() => setAiOpen(true)}
              aria-label="Open NightAI assistant"
              className="flex h-12 items-center rounded-full text-white shadow-[0_10px_26px_rgb(29_95_214_/_0.42)] transition-[filter] hover:brightness-105"
              style={{ background: 'linear-gradient(140deg,#1d5fd6,#2f74ea)' }}
            >
              <span className="flex size-12 shrink-0 items-center justify-center">
                <BrandMark monochrome className="size-6" />
              </span>
              <span
                className={cn(
                  'overflow-hidden whitespace-nowrap text-[14.5px] font-bold transition-all duration-300 ease-out',
                  fabLabelShown ? 'max-w-[150px] pr-5 opacity-100' : 'max-w-0 pr-0 opacity-0',
                )}
              >
                Ask NightAI
              </span>
            </button>
          </div>
        </div>
      )}

      <NightAIDrawer open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
